/**
 * Spatial hash — uniform grid over the XZ plane for group-level queries.
 *
 * Scale rule: the AI (and, eventually, combat) must never do pairwise
 * group-vs-group scans. With 20,000+ soldiers the sim thinks in
 * formations; a few hundred formations in a grid of ~30 m cells makes
 * aggro / target-acquisition / discovery queries O(nearby) instead of
 * O(groups²). Render-free, no allocation beyond the cell buckets, and
 * rebuilt per tick from the live arrays (cheap: one insert per group).
 */
export class SpatialHash<T> {
  private readonly cells = new Map<string, T[]>();

  constructor(private readonly cellSize: number) {}

  clear(): void {
    this.cells.clear();
  }

  insert(x: number, z: number, value: T): void {
    const key = this.key(x, z);
    let bucket = this.cells.get(key);
    if (!bucket) {
      bucket = [];
      this.cells.set(key, bucket);
    }
    bucket.push(value);
  }

  /**
   * Call `fn` for every value in cells intersecting the circle.
   * The callback receives candidates — the caller still applies its own
   * exact range / team / state filter.
   */
  forEachInRadius(
    x: number,
    z: number,
    radius: number,
    fn: (value: T) => void,
  ): void {
    const cs = this.cellSize;
    const minCx = Math.floor((x - radius) / cs);
    const maxCx = Math.floor((x + radius) / cs);
    const minCz = Math.floor((z - radius) / cs);
    const maxCz = Math.floor((z + radius) / cs);
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const bucket = this.cells.get(cx + "," + cz);
        if (!bucket) continue;
        for (const v of bucket) fn(v);
      }
    }
  }

  private key(x: number, z: number): string {
    return (
      Math.floor(x / this.cellSize) + "," + Math.floor(z / this.cellSize)
    );
  }
}
